// cspell: ignore logcat
// cspell: ignore usec
import { AdbCommandBase, AdbSubprocessNoneProtocol } from "@yume-chan/adb";
import { BufferedTransformStream, DecodeUtf8Stream, SplitStringStream, WrapReadableStream, WritableStream, } from "@yume-chan/stream-extra";
import Struct, { decodeUtf8 } from "@yume-chan/struct";
// `adb logcat` is an alias to `adb shell logcat`
// so instead of adding to core library, it's implemented here
// https://cs.android.com/android/platform/superproject/+/master:system/logging/liblog/include/android/log.h;l=141;drc=82b5738732161dbaafb2e2f25cce19cd26b9157d
export var LogId;
(function (LogId) {
    LogId[LogId["All"] = -1] = "All";
    LogId[LogId["Main"] = 0] = "Main";
    LogId[LogId["Radio"] = 1] = "Radio";
    LogId[LogId["Events"] = 2] = "Events";
    LogId[LogId["System"] = 3] = "System";
    LogId[LogId["Crash"] = 4] = "Crash";
    LogId[LogId["Stats"] = 5] = "Stats";
    LogId[LogId["Security"] = 6] = "Security";
    LogId[LogId["Kernel"] = 7] = "Kernel";
})(LogId = LogId || (LogId = {}));
// https://cs.android.com/android/platform/superproject/+/master:system/logging/liblog/include/android/log.h;l=73;drc=82b5738732161dbaafb2e2f25cce19cd26b9157d
export var AndroidLogPriority;
(function (AndroidLogPriority) {
    AndroidLogPriority[AndroidLogPriority["Unknown"] = 0] = "Unknown";
    AndroidLogPriority[AndroidLogPriority["Default"] = 1] = "Default";
    AndroidLogPriority[AndroidLogPriority["Verbose"] = 2] = "Verbose";
    AndroidLogPriority[AndroidLogPriority["Debug"] = 3] = "Debug";
    AndroidLogPriority[AndroidLogPriority["Info"] = 4] = "Info";
    AndroidLogPriority[AndroidLogPriority["Warn"] = 5] = "Warn";
    AndroidLogPriority[AndroidLogPriority["Error"] = 6] = "Error";
    AndroidLogPriority[AndroidLogPriority["Fatal"] = 7] = "Fatal";
    AndroidLogPriority[AndroidLogPriority["Silent"] = 8] = "Silent";
})(AndroidLogPriority = AndroidLogPriority || (AndroidLogPriority = {}));
// https://cs.android.com/android/platform/superproject/+/master:system/logging/liblog/logprint.cpp;l=140;drc=8dbf3b2bb6b6d1652d9797e477b9abd03278bb79
export const AndroidLogPriorityToCharacter = {
    [AndroidLogPriority.Unknown]: "?",
    [AndroidLogPriority.Default]: "?",
    [AndroidLogPriority.Verbose]: "V",
    [AndroidLogPriority.Debug]: "D",
    [AndroidLogPriority.Info]: "I",
    [AndroidLogPriority.Warn]: "W",
    [AndroidLogPriority.Error]: "E",
    [AndroidLogPriority.Fatal]: "F",
    [AndroidLogPriority.Silent]: "S",
};
export var LogcatFormat;
(function (LogcatFormat) {
    LogcatFormat[LogcatFormat["Brief"] = 0] = "Brief";
    LogcatFormat[LogcatFormat["Process"] = 1] = "Process";
    LogcatFormat[LogcatFormat["Tag"] = 2] = "Tag";
    LogcatFormat[LogcatFormat["Thread"] = 3] = "Thread";
    LogcatFormat[LogcatFormat["Raw"] = 4] = "Raw";
    LogcatFormat[LogcatFormat["Time"] = 5] = "Time";
    LogcatFormat[LogcatFormat["ThreadTime"] = 6] = "ThreadTime";
    LogcatFormat[LogcatFormat["Long"] = 7] = "Long";
})(LogcatFormat = LogcatFormat || (LogcatFormat = {}));
const NANOSECONDS_PER_SECOND = BigInt(1e9);
// https://cs.android.com/android/platform/superproject/+/master:system/logging/liblog/include/log/log_read.h;l=39;drc=82b5738732161dbaafb2e2f25cce19cd26b9157d
export const LoggerEntry = new Struct({ littleEndian: true })
    .uint16("payloadSize")
    .uint16("headerSize")
    .int32("pid")
    .uint32("tid")
    .uint32("second")
    .uint32("nanoseconds")
    .uint32("logId")
    .uint32("uid")
    .extra({
    get timestamp() {
        return (BigInt(this.second) * NANOSECONDS_PER_SECOND +
            BigInt(this.nanoseconds));
    },
});
// https://cs.android.com/android/platform/superproject/+/master:system/logging/liblog/logprint.cpp;l=1415;drc=8dbf3b2bb6b6d1652d9797e477b9abd03278bb79
export function formatAndroidLogEntry(entry, format = LogcatFormat.Brief, modifier) {
    const uid = modifier?.uid ? `${entry.uid.toString().padStart(5)}:` : "";
    switch (format) {
        // TODO: implement other formats
        default: {
            // prettier-ignore
            const text = `${AndroidLogPriorityToCharacter[entry.priority]}/${entry.tag.padEnd(8)}(${uid}${entry.pid.toString().padStart(5)}): ${entry.message}`;
            return text;
        }
    }
}
function findTagEnd(payload) {
    for (const separator of [0, " ".charCodeAt(0), ":".charCodeAt(0)]) {
        const index = payload.indexOf(separator);
        if (index !== -1) {
            return index;
        }
    }
    const index = payload.findIndex((x) => x >= 0x7f);
    if (index !== -1) {
        return index;
    }
    return payload.length;
}
export async function deserializeAndroidLogEntry(stream) {
    const entry = (await LoggerEntry.deserialize(stream));
    if (entry.headerSize !== LoggerEntry.size) {
        await stream.read(entry.headerSize - LoggerEntry.size);
    }
    let payload = await stream.read(entry.payloadSize);
    // https://cs.android.com/android/platform/superproject/+/master:system/logging/logcat/logcat.cpp;l=193-194;drc=bbe77d66e7bee8bd1f0bc7e5492b5376b0207ef6
    // TODO: payload for some log IDs are in binary format.
    entry.priority = payload[0];
    payload = payload.subarray(1);
    const tagEnd = findTagEnd(payload);
    entry.tag = decodeUtf8(payload.subarray(0, tagEnd));
    entry.message =
        tagEnd < payload.length - 1
            ? decodeUtf8(payload.subarray(tagEnd + 1))
            : "";
    return entry;
}
export class Logcat extends AdbCommandBase {
    static logIdToName(id) {
        return LogId[id];
    }
    static logNameToId(name) {
        const key = name[0].toUpperCase() + name.substring(1);
        return LogId[key];
    }
    static joinLogId(ids) {
        return ids.map((id) => Logcat.logIdToName(id)).join(",");
    }
    static parseSize(value, multiplier) {
        const MULTIPLIERS = ["", "Ki", "Mi", "Gi"];
        return value * 1024 ** (MULTIPLIERS.indexOf(multiplier) || 0);
    }
    // TODO: logcat: Support output format before Android 10
    // ref https://android-review.googlesource.com/c/platform/system/core/+/748128
    static LOG_SIZE_REGEX_10 = /(.*): ring buffer is (.*) (.*)B \((.*) (.*)B consumed\), max entry is (.*) B, max payload is (.*) B/;
    // Android 11 added `readable` part
    // ref https://android-review.googlesource.com/c/platform/system/core/+/1390940
    static LOG_SIZE_REGEX_11 = /(.*): ring buffer is (.*) (.*)B \((.*) (.*)B consumed, (.*) (.*)B readable\), max entry is (.*) B, max payload is (.*) B/;
    async getLogSize(ids) {
        const { stdout } = await this.adb.subprocess.spawn([
            "logcat",
            "-g",
            ...(ids ? ["-b", Logcat.joinLogId(ids)] : []),
        ]);
        const result = [];
        await stdout
            .pipeThrough(new DecodeUtf8Stream())
            .pipeThrough(new SplitStringStream("\n"))
            .pipeTo(new WritableStream({
            write(chunk) {
                let match = chunk.match(Logcat.LOG_SIZE_REGEX_11);
                if (match) {
                    result.push({
                        id: Logcat.logNameToId(match[1]),
                        size: Logcat.parseSize(Number.parseInt(match[2], 10), match[3]),
                        readable: Logcat.parseSize(Number.parseInt(match[6], 10), match[7]),
                        consumed: Logcat.parseSize(Number.parseInt(match[4], 10), match[5]),
                        maxEntrySize: parseInt(match[8], 10),
                        maxPayloadSize: parseInt(match[9], 10),
                    });
                }
                match = chunk.match(Logcat.LOG_SIZE_REGEX_10);
                if (match) {
                    result.push({
                        id: Logcat.logNameToId(match[1]),
                        size: Logcat.parseSize(Number.parseInt(match[2], 10), match[3]),
                        consumed: Logcat.parseSize(Number.parseInt(match[4], 10), match[5]),
                        maxEntrySize: parseInt(match[6], 10),
                        maxPayloadSize: parseInt(match[7], 10),
                    });
                }
            },
        }));
        return result;
    }
    async clear(ids) {
        await this.adb.subprocess.spawnAndWait([
            "logcat",
            "-c",
            ...(ids ? ["-b", Logcat.joinLogId(ids)] : []),
        ]);
    }
    binary(options) {
        return new WrapReadableStream(async () => {
            // TODO: make `spawn` return synchronously with streams pending
            // so it's easier to chain them.
            const { stdout } = await this.adb.subprocess.spawn([
                "logcat",
                "-B",
                ...(options?.pid ? ["--pid", options.pid.toString()] : []),
                ...(options?.ids
                    ? ["-b", Logcat.joinLogId(options.ids)]
                    : []),
            ], {
                // PERF: None protocol is 150% faster then Shell protocol
                protocols: [AdbSubprocessNoneProtocol],
            });
            return stdout;
        }).pipeThrough(new BufferedTransformStream((stream) => {
            return deserializeAndroidLogEntry(stream);
        }));
    }
}
//# sourceMappingURL=logcat.js.map